// src/pages/AdminMarketplace.js
import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, orderBy, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../contexts/authContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ImageUploader from '../components/ImageUploader';

const AdminMarketplace = () => {
  const { user, loading, isAdmin, ready } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [filter, setFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [editMessage, setEditMessage] = useState('');
  // Date assignment state
  const [officialGames, setOfficialGames] = useState([]);
  const [editingDateGameId, setEditingDateGameId] = useState(null);
  const [tempDateValue, setTempDateValue] = useState('');
  const [dateAssignmentMessage, setDateAssignmentMessage] = useState('');
  const [editingTitleGameId, setEditingTitleGameId] = useState(null);
  const [tempTitleValue, setTempTitleValue] = useState('');

  const fetchMarketplaceGames = useCallback(async () => {
    try {
      setLoadingContent(true);
      
      let gamesQuery;
      let gamesData = [];
      
      try {
        console.log('🔍 AdminMarketplace: Attempting primary query for filter:', filter);
        if (filter === 'pending') {
          gamesQuery = query(
            collection(db, 'games', 'content', 'triviaGames'),
            where('marketplaceData.approvalStatus', '==', 'pending'),
            orderBy('createdAt', 'desc')
          );
        } else if (filter === 'approved') {
          gamesQuery = query(
            collection(db, 'games', 'content', 'triviaGames'),
            where('marketplaceData.approvalStatus', '==', 'approved'),
            orderBy('createdAt', 'desc')
          );
        } else if (filter === 'rejected') {
          gamesQuery = query(
            collection(db, 'games', 'content', 'triviaGames'),
            where('marketplaceData.approvalStatus', '==', 'rejected'),
            orderBy('createdAt', 'desc')
          );
        } else {
          // All games
          gamesQuery = query(
            collection(db, 'games', 'content', 'triviaGames'),
            orderBy('createdAt', 'desc')
          );
        }
        
        const gamesSnapshot = await getDocs(gamesQuery);
        gamesData = gamesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('✅ AdminMarketplace: Primary query successful, got', gamesData.length, 'games');
      } catch (error) {
        console.log('❌ AdminMarketplace: Primary query failed:', error.message);
        console.log('🔄 AdminMarketplace: Using fallback method...');
        // Fallback: fetch all games and filter client-side
        try {
          const allGamesQuery = query(
            collection(db, 'games', 'content', 'triviaGames'),
            orderBy('createdAt', 'desc')
          );
          const allGamesSnapshot = await getDocs(allGamesQuery);
          const allGames = allGamesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log('🔍 AdminMarketplace: Fallback query got', allGames.length, 'games from marketplace collection');
          
          // Filter based on current filter
          if (filter === 'pending') {
            gamesData = allGames.filter(game => game.marketplaceData?.approvalStatus === 'pending');
          } else if (filter === 'approved') {
            gamesData = allGames.filter(game => game.marketplaceData?.approvalStatus === 'approved');
          } else if (filter === 'rejected') {
            gamesData = allGames.filter(game => game.marketplaceData?.approvalStatus === 'rejected');
          } else {
            gamesData = allGames;
          }
          console.log('✅ AdminMarketplace: Fallback filtering complete, got', gamesData.length, 'games');
        } catch (fallbackError) {
          console.error('❌ AdminMarketplace: Fallback query failed:', fallbackError);
        }
      }
      
      console.log('🔍 AdminMarketplace: Fetched games:', gamesData.map(g => ({ id: g.id, title: g.title })));
      console.log('🔍 AdminMarketplace: Looking for "test 8 sept" game...');
      const testGame = gamesData.find(g => g.title && g.title.toLowerCase().includes('test 8 sept'));
      if (testGame) {
        console.log('✅ Found "test 8 sept" game with ID:', testGame.id);
      } else {
        console.log('❌ "test 8 sept" game not found in fetched games');
      }
      setGames(gamesData);
    } catch (error) {
      console.error('Error fetching marketplace games:', error);
    } finally {
      setLoadingContent(false);
    }
  }, [filter]);

  useEffect(() => {
    // Wait for auth to be ready
    if (!ready) return;
    
    // Only redirect if we're sure the user is not an admin (use comprehensive check)
    if (!loading && user && !isAdmin) {
      console.log('❌ AdminMarketplace: User is not admin, redirecting');
      navigate('/not-authorized');
      return;
    }
    
    // Only fetch content if user is admin
    if (user && isAdmin && filter !== 'date-assignment') {
      fetchMarketplaceGames();
    }
  }, [user, isAdmin, loading, ready, navigate, fetchMarketplaceGames, filter]);

  // Fetch official games for date assignment
  const fetchOfficialGames = useCallback(async () => {
    try {
      console.log('📅 Fetching official games for date assignment...');
      const allGamesQuery = query(
        collection(db, 'games', 'content', 'triviaGames'),
        orderBy('createdAt', 'desc')
      );
      const allGamesSnapshot = await getDocs(allGamesQuery);
      const allGames = allGamesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter for official games (isLeagueSponsored: true)
      const official = allGames.filter(game => game.isLeagueSponsored === true);
      console.log('✅ Found', official.length, 'official games');
      setOfficialGames(official);
    } catch (error) {
      console.error('Error fetching official games:', error);
    }
  }, []);

  // Fetch official games when switching to date assignment tab
  useEffect(() => {
    if (filter === 'date-assignment' && user && isAdmin && ready) {
      fetchOfficialGames();
    }
  }, [filter, user, isAdmin, ready, fetchOfficialGames]);

  const handleApprove = async (game) => {
    try {
      await updateDoc(doc(db, 'games', 'content', 'triviaGames', game.id), {
        'marketplaceData.approvalStatus': 'approved',
        'marketplaceData.approvedAt': new Date(),
        'marketplaceData.approvedBy': user.uid,
        status: 'public',
        updatedAt: new Date()
      });
      
      fetchMarketplaceGames();
      setShowDetailModal(false);
      setSelectedGame(null);
    } catch (error) {
      console.error('Error approving game:', error);
      alert('Error approving game');
    }
  };

  const handleReject = async (game, reason) => {
    try {
      await updateDoc(doc(db, 'games', 'content', 'triviaGames', game.id), {
        'marketplaceData.approvalStatus': 'rejected',
        'marketplaceData.rejectedAt': new Date(),
        'marketplaceData.rejectedBy': user.uid,
        'marketplaceData.rejectionReason': reason,
        status: 'rejected',
        updatedAt: new Date()
      });
      
      fetchMarketplaceGames();
      setShowDetailModal(false);
      setSelectedGame(null);
    } catch (error) {
      console.error('Error rejecting game:', error);
      alert('Error rejecting game');
    }
  };

  const handleDelete = async (game) => {
    if (window.confirm('Are you sure you want to delete this game? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'games', 'content', 'triviaGames', game.id));
        fetchMarketplaceGames();
        setShowDetailModal(false);
        setSelectedGame(null);
      } catch (error) {
        console.error('Error deleting game:', error);
        alert('Error deleting game');
      }
    }
  };

  // Date assignment functions
  const handleStartDateEdit = (game) => {
    setEditingDateGameId(game.id);
    setTempDateValue(game.gameDate || '');
    setDateAssignmentMessage('');
  };

  const handleCancelDateEdit = () => {
    setEditingDateGameId(null);
    setTempDateValue('');
    setDateAssignmentMessage('');
  };

  const handleAssignDate = async (gameId, dateValue) => {
    if (!dateValue) {
      setDateAssignmentMessage('❌ Please enter a date');
      return;
    }

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateValue)) {
      setDateAssignmentMessage('❌ Date must be in YYYY-MM-DD format (e.g., 2025-12-02)');
      return;
    }

    // Validate date is valid
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      setDateAssignmentMessage('❌ Invalid date');
      return;
    }

    try {
      setSaving(true);
      setDateAssignmentMessage('⏳ Assigning date...');
      
      await updateDoc(doc(db, 'games', 'content', 'triviaGames', gameId), {
        gameDate: dateValue,
        updatedAt: new Date()
      });

      setDateAssignmentMessage('✅ Date assigned successfully!');
      
      // Refresh official games list
      await fetchOfficialGames();
      
      // Clear editing state after a short delay
      setTimeout(() => {
        setEditingDateGameId(null);
        setTempDateValue('');
        setDateAssignmentMessage('');
      }, 2000);
    } catch (error) {
      console.error('Error assigning date:', error);
      setDateAssignmentMessage('❌ Error assigning date: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDate = async (gameId) => {
    if (!window.confirm('Remove date assignment from this game?')) {
      return;
    }

    try {
      setSaving(true);
      setDateAssignmentMessage('⏳ Removing date...');
      
      await updateDoc(doc(db, 'games', 'content', 'triviaGames', gameId), {
        gameDate: null,
        updatedAt: new Date()
      });

      setDateAssignmentMessage('✅ Date removed successfully!');
      
      // Refresh official games list
      await fetchOfficialGames();
      
      setTimeout(() => {
        setDateAssignmentMessage('');
      }, 2000);
    } catch (error) {
      console.error('Error removing date:', error);
      setDateAssignmentMessage('❌ Error removing date: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Title editing functions
  const handleStartTitleEdit = (game) => {
    setEditingTitleGameId(game.id);
    setTempTitleValue(game.title || '');
    setDateAssignmentMessage('');
  };

  const handleCancelTitleEdit = () => {
    setEditingTitleGameId(null);
    setTempTitleValue('');
    setDateAssignmentMessage('');
  };

  const handleUpdateTitle = async (gameId, newTitle) => {
    if (!newTitle || newTitle.trim() === '') {
      setDateAssignmentMessage('❌ Title cannot be empty');
      return;
    }

    try {
      setSaving(true);
      setDateAssignmentMessage('⏳ Updating title...');
      
      await updateDoc(doc(db, 'games', 'content', 'triviaGames', gameId), {
        title: newTitle.trim(),
        updatedAt: new Date()
      });

      setDateAssignmentMessage('✅ Title updated successfully!');
      
      // Refresh official games list
      await fetchOfficialGames();
      
      // Clear editing state after a short delay
      setTimeout(() => {
        setEditingTitleGameId(null);
        setTempTitleValue('');
        setDateAssignmentMessage('');
      }, 2000);
    } catch (error) {
      console.error('Error updating title:', error);
      setDateAssignmentMessage('❌ Error updating title: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const openDetailModal = (game) => {
    setSelectedGame(game);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedGame(null);
  };

  const openEditModal = (game) => {
    setEditingGame({ ...game });
    setShowEditModal(true);
    setEditMessage('');
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingGame(null);
    setEditMessage('');
  };

  const handleEditChange = (section, index, field, value) => {
    const updated = { ...editingGame };
    if (Array.isArray(updated[section])) {
      updated[section] = updated[section].map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      );
    } else {
      updated[section][field] = value;
    }
    setEditingGame(updated);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    setEditMessage('');
    
    try {
      // Remove any undefined values to avoid Firestore errors
      const cleanGameData = Object.fromEntries(
        Object.entries(editingGame).filter(([_, value]) => value !== undefined)
      );
      
      await updateDoc(doc(db, 'games', 'content', 'triviaGames', editingGame.id), {
        ...cleanGameData,
        updatedAt: new Date(),
        lastEditedBy: user.uid,
        lastEditedAt: new Date()
      });
      
      setEditMessage('✅ Game updated successfully!');
      fetchMarketplaceGames(); // Refresh the list
      
      setTimeout(() => {
        closeEditModal();
      }, 2000);
    } catch (error) {
      console.error('Error updating game:', error);
      setEditMessage('❌ Failed to update game: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = (section, index, field, imageUrl) => {
    const updated = { ...editingGame };
    if (Array.isArray(updated[section])) {
      updated[section] = updated[section].map((item, i) =>
        i === index ? { ...item, [field]: imageUrl } : item
      );
    } else {
      updated[section][field] = imageUrl;
    }
    setEditingGame(updated);
  };

  const handleImageDelete = (section, index, field) => {
    const updated = { ...editingGame };
    if (Array.isArray(updated[section])) {
      updated[section] = updated[section].map((item, i) =>
        i === index ? { ...item, [field]: '' } : item
      );
    } else {
      updated[section][field] = '';
    }
    setEditingGame(updated);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-400';
      case 'pending': return 'text-yellow-400';
      case 'rejected': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return '✅';
      case 'pending': return '⏳';
      case 'rejected': return '❌';
      default: return '❓';
    }
  };

  const GameCard = ({ game }) => (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 hover:border-gray-600 transition-colors">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white mb-2">{game.title}</h3>
          <p className="text-gray-300 text-sm mb-2">{game.description || 'No description'}</p>
          <div className="flex items-center space-x-4 text-sm text-gray-400">
            <span>By: {game.creatorName || 'Unknown'}</span>
            <span>Price: ${game.marketplaceData?.price || 0}</span>
                                    <span>Questions: 22</span>
          </div>
        </div>
        <div className={`text-right ${getStatusColor(game.marketplaceData?.approvalStatus)}`}>
          <div className="text-2xl mb-1">{getStatusIcon(game.marketplaceData?.approvalStatus)}</div>
          <div className="text-xs font-medium uppercase">
            {game.marketplaceData?.approvalStatus || 'No Status'}
          </div>
        </div>
      </div>
      
      <div className="flex justify-between items-center">
        <div className="text-xs text-gray-500">
          Created: {game.createdAt?.toDate?.() ? game.createdAt.toDate().toLocaleDateString() : 'Unknown'}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => openDetailModal(game)}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
          >
            View Details
          </button>
          <button
            onClick={() => openEditModal(game)}
            className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 transition-colors"
          >
            Quick Edit
          </button>
          <button
            onClick={() => {
              console.log('🔍 Full Edit button clicked for game:', { id: game.id, title: game.title });
              navigate(`/edit-game/${game.id}`);
            }}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
          >
            Full Edit
          </button>
          {game.marketplaceData?.approvalStatus === 'pending' && (
            <>
              <button
                onClick={() => handleApprove(game)}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
              >
                Approve
              </button>
              <button
                onClick={() => handleReject(game, 'Rejected by admin')}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
              >
                Reject
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (loadingContent) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading marketplace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <div className="p-8">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Admin Marketplace</h1>
              <p className="text-gray-300">Manage all marketplace games with full admin privileges</p>
            </div>
            <button
              onClick={fetchMarketplaceGames}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              🔄 Refresh Games
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6">
          <div className="flex space-x-4 border-b border-gray-700">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 font-medium transition-colors ${
                filter === 'all' 
                  ? 'text-green-400 border-b-2 border-green-400' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              All Games ({games.length})
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 font-medium transition-colors ${
                filter === 'pending' 
                  ? 'text-green-400 border-b-2 border-green-400' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Pending Review
            </button>
            <button
              onClick={() => setFilter('approved')}
              className={`px-4 py-2 font-medium transition-colors ${
                filter === 'approved' 
                  ? 'text-green-400 border-b-2 border-green-400' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Approved
            </button>
            <button
              onClick={() => setFilter('rejected')}
              className={`px-4 py-2 font-medium transition-colors ${
                filter === 'rejected' 
                  ? 'text-green-400 border-b-2 border-green-400' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Rejected
            </button>
            <button
              onClick={() => {
                setFilter('date-assignment');
                fetchOfficialGames();
              }}
              className={`px-4 py-2 font-medium transition-colors ${
                filter === 'date-assignment' 
                  ? 'text-green-400 border-b-2 border-green-400' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              📅 Date Assignment
            </button>
          </div>
        </div>

        {/* Date Assignment View */}
        {filter === 'date-assignment' && (
          <div className="space-y-6">
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">Assign Dates to Official Games</h2>
              <p className="text-gray-400 mb-4">
                Assign calendar dates to official games. Games with dates will appear in the calendar view.
                Games without dates will still appear in the regular marketplace.
              </p>
              
              {dateAssignmentMessage && (
                <div className={`mb-4 p-3 rounded ${
                  dateAssignmentMessage.includes('✅') 
                    ? 'bg-green-900 text-green-200' 
                    : dateAssignmentMessage.includes('❌')
                    ? 'bg-red-900 text-red-200'
                    : 'bg-yellow-900 text-yellow-200'
                }`}>
                  {dateAssignmentMessage}
                </div>
              )}

              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search games by title..."
                  onChange={(e) => {
                    const searchTerm = e.target.value.toLowerCase();
                    if (searchTerm === '') {
                      fetchOfficialGames();
                    } else {
                      const filtered = officialGames.filter(game => 
                        game.title?.toLowerCase().includes(searchTerm)
                      );
                      setOfficialGames(filtered);
                    }
                  }}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-3">
                {officialGames.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No official games found</p>
                  </div>
                ) : (
                  officialGames.map(game => (
                    <div key={game.id} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          {editingTitleGameId === game.id ? (
                            <div className="mb-2">
                              <input
                                type="text"
                                value={tempTitleValue}
                                onChange={(e) => setTempTitleValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleUpdateTitle(game.id, tempTitleValue);
                                  } else if (e.key === 'Escape') {
                                    handleCancelTitleEdit();
                                  }
                                }}
                                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                              />
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className="text-lg font-semibold text-white">{game.title || 'Untitled Game'}</h3>
                              <button
                                onClick={() => handleStartTitleEdit(game)}
                                className="text-gray-400 hover:text-blue-400 text-sm"
                                title="Edit title"
                              >
                                ✏️
                              </button>
                            </div>
                          )}
                          <div className="flex items-center space-x-4 text-sm text-gray-400">
                            <span>ID: {game.id}</span>
                            {game.gameDate && (
                              <span className="text-green-400">📅 {game.gameDate}</span>
                            )}
                            {!game.gameDate && (
                              <span className="text-yellow-400">⚠️ No date assigned</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {editingTitleGameId === game.id ? (
                            <>
                              <button
                                onClick={() => handleUpdateTitle(game.id, tempTitleValue)}
                                disabled={saving}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 text-sm"
                              >
                                {saving ? 'Saving...' : 'Save Title'}
                              </button>
                              <button
                                onClick={handleCancelTitleEdit}
                                disabled={saving}
                                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors disabled:opacity-50 text-sm"
                              >
                                Cancel
                              </button>
                            </>
                          ) : editingDateGameId === game.id ? (
                            <>
                              <input
                                type="date"
                                value={tempDateValue}
                                onChange={(e) => setTempDateValue(e.target.value)}
                                className="px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="YYYY-MM-DD"
                              />
                              <button
                                onClick={() => handleAssignDate(game.id, tempDateValue)}
                                disabled={saving}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 text-sm"
                              >
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={handleCancelDateEdit}
                                disabled={saving}
                                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors disabled:opacity-50 text-sm"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleStartDateEdit(game)}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                              >
                                {game.gameDate ? 'Change Date' : 'Assign Date'}
                              </button>
                              {game.gameDate && (
                                <button
                                  onClick={() => handleRemoveDate(game.id)}
                                  disabled={saving}
                                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 text-sm"
                                >
                                  Remove
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Games Grid */}
        {filter !== 'date-assignment' && (
          <div className="space-y-6">
            {games.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">🛒</div>
                <p className="text-gray-300">No games found in this category</p>
              </div>
            ) : (
              games.map(game => <GameCard key={game.id} game={game} />)
            )}
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedGame && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-white">{selectedGame.title}</h2>
                <button
                  onClick={closeDetailModal}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Game Details</h3>
                  <div className="bg-gray-700 rounded p-4 space-y-2">
                    <p><strong className="text-gray-300">Creator:</strong> {selectedGame.creatorName || 'Unknown'}</p>
                    <p><strong className="text-gray-300">Price:</strong> ${selectedGame.marketplaceData?.price || 0}</p>
                    <p><strong className="text-gray-300">Status:</strong> {selectedGame.marketplaceData?.approvalStatus || 'No Status'}</p>
                    <p><strong className="text-gray-300">Questions:</strong> 22</p>
                    <p><strong className="text-gray-300">Description:</strong> {selectedGame.description || 'No description'}</p>
                  </div>
                </div>

                {selectedGame.questions && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Questions Preview</h3>
                    <div className="bg-gray-700 rounded p-4 space-y-3">
                      {selectedGame.questions.slice(0, 3).map((question, index) => (
                        <div key={index} className="border-l-4 border-gray-600 pl-3">
                          <p className="text-white font-medium">Q{index + 1}: {question.question}</p>
                          <p className="text-gray-300 text-sm">A: {question.answer}</p>
                        </div>
                      ))}
                      {selectedGame.questions.length > 3 && (
                        <p className="text-gray-400 text-sm">... and {selectedGame.questions.length - 3} more questions</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-700">
                  <button
                    onClick={() => openEditModal(selectedGame)}
                    className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                  >
                    Edit Game
                  </button>
                  {selectedGame.marketplaceData?.approvalStatus === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApprove(selectedGame)}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                      >
                        Approve Game
                      </button>
                      <button
                        onClick={() => handleReject(selectedGame, 'Rejected by admin')}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      >
                        Reject Game
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleDelete(selectedGame)}
                    className="px-4 py-2 bg-red-800 text-white rounded hover:bg-red-900 transition-colors"
                  >
                    Delete Game
                  </button>
                  <button
                    onClick={closeDetailModal}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Game Modal */}
        {showEditModal && editingGame && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-white">Edit Game: {editingGame.title}</h2>
                  <button
                    onClick={closeEditModal}
                    className="text-gray-400 hover:text-white text-2xl"
                  >
                    ×
                  </button>
                </div>

                {editMessage && (
                  <div className={`mb-4 p-3 rounded ${
                    editMessage.includes('✅') ? 'bg-green-900 text-green-200' : 'bg-red-900 text-red-200'
                  }`}>
                    {editMessage}
                  </div>
                )}

                <div className="space-y-6">
                  {/* Basic Game Info */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Basic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-1">Title</label>
                        <input
                          type="text"
                          value={editingGame.title || ''}
                          onChange={(e) => setEditingGame({...editingGame, title: e.target.value})}
                          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-1">Description</label>
                        <textarea
                          value={editingGame.description || ''}
                          onChange={(e) => setEditingGame({...editingGame, description: e.target.value})}
                          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                          rows="3"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-1">Price ($)</label>
                        <input
                          type="number"
                          value={editingGame.marketplaceData?.price || 0}
                          onChange={(e) => setEditingGame({
                            ...editingGame, 
                            marketplaceData: {
                              ...editingGame.marketplaceData,
                              price: parseFloat(e.target.value) || 0
                            }
                          })}
                          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-300 text-sm font-medium mb-1">Approval Status</label>
                        <select
                          value={editingGame.marketplaceData?.approvalStatus || 'pending'}
                          onChange={(e) => setEditingGame({
                            ...editingGame, 
                            marketplaceData: {
                              ...editingGame.marketplaceData,
                              approvalStatus: e.target.value
                            }
                          })}
                          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                        >
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Questions Editor */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Questions</h3>
                    
                                         {/* Quarter 1 */}
                     {editingGame.quarter1 && (
                       <div className="mb-6">
                         <h4 className="text-md font-medium text-gray-300 mb-3">Quarter 1</h4>
                         {editingGame.quarter1.map((question, index) => (
                           <div key={index} className="bg-gray-700 p-4 rounded mb-3">
                             <div className="mb-3">
                               <input
                                 type="text"
                                 placeholder="Category"
                                 value={question.category || ''}
                                 onChange={(e) => handleEditChange('quarter1', index, 'category', e.target.value)}
                                 className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                               />
                             </div>
                             <textarea
                               placeholder="Question"
                               value={question.question || ''}
                               onChange={(e) => handleEditChange('quarter1', index, 'question', e.target.value)}
                               className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm mb-2"
                               rows="2"
                             />
                             <input
                               type="text"
                               placeholder="Answer"
                               value={question.answer || ''}
                               onChange={(e) => handleEditChange('quarter1', index, 'answer', e.target.value)}
                               className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm mb-3"
                             />
                             
                             {/* Question Image */}
                             <div className="mb-3">
                               <label className="block text-gray-300 text-sm font-medium mb-1">Question Image</label>
                               <ImageUploader
                                 label="Upload Question Image"
                                 storagePath={`game-images/admin/${editingGame.id}/quarter1-q${index + 1}-question`}
                                 onUpload={(url) => handleImageUpload('quarter1', index, 'questionImage', url)}
                               />
                               {question.questionImage && (
                                 <div className="relative mt-2">
                                   <img src={question.questionImage} alt="" className="max-h-32 rounded border" />
                                   <button
                                     className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                                     onClick={() => handleImageDelete('quarter1', index, 'questionImage')}
                                   >
                                     ✕
                                   </button>
                                 </div>
                               )}
                             </div>
                             
                             {/* Answer Image */}
                             <div className="mb-3">
                               <label className="block text-gray-300 text-sm font-medium mb-1">Answer Image</label>
                               <ImageUploader
                                 label="Upload Answer Image"
                                 storagePath={`game-images/admin/${editingGame.id}/quarter1-q${index + 1}-answer`}
                                 onUpload={(url) => handleImageUpload('quarter1', index, 'answerImage', url)}
                               />
                               {question.answerImage && (
                                 <div className="relative mt-2">
                                   <img src={question.answerImage} alt="" className="max-h-32 rounded border" />
                                   <button
                                     className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                                     onClick={() => handleImageDelete('quarter1', index, 'answerImage')}
                                   >
                                     ✕
                                   </button>
                                 </div>
                               )}
                             </div>
                             
                             {/* Creator Comment */}
                             <div className="mb-3">
                               <label className="block text-gray-300 text-sm font-medium mb-1">
                                 💡 Creator Comment (Optional)
                               </label>
                               <textarea
                                 placeholder="Explain hints, song clues, or provide context for this specific question..."
                                 value={question.creatorComment || ''}
                                 onChange={(e) => handleEditChange('quarter1', index, 'creatorComment', e.target.value)}
                                 className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                                 rows="3"
                               />
                               <p className="text-xs text-gray-400 mt-1">
                                 Optional: Explain how song hints relate to the answer, provide context, or share inside jokes
                               </p>
                             </div>
                           </div>
                         ))}
                       </div>
                     )}

                                         {/* Quarter 2 */}
                     {editingGame.quarter2 && (
                       <div className="mb-6">
                         <h4 className="text-md font-medium text-gray-300 mb-3">Quarter 2</h4>
                         {editingGame.quarter2.map((question, index) => (
                           <div key={index} className="bg-gray-700 p-4 rounded mb-3">
                             <div className="mb-3">
                               <input
                                 type="text"
                                 placeholder="Category"
                                 value={question.category || ''}
                                 onChange={(e) => handleEditChange('quarter2', index, 'category', e.target.value)}
                                 className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                               />
                             </div>
                             <textarea
                               placeholder="Question"
                               value={question.question || ''}
                               onChange={(e) => handleEditChange('quarter2', index, 'question', e.target.value)}
                               className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm mb-2"
                               rows="2"
                             />
                             <input
                               type="text"
                               placeholder="Answer"
                               value={question.answer || ''}
                               onChange={(e) => handleEditChange('quarter2', index, 'answer', e.target.value)}
                               className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm mb-3"
                             />
                             
                             {/* Question Image */}
                             <div className="mb-3">
                               <label className="block text-gray-300 text-sm font-medium mb-1">Question Image</label>
                               <ImageUploader
                                 label="Upload Question Image"
                                 storagePath={`game-images/admin/${editingGame.id}/quarter2-q${index + 1}-question`}
                                 onUpload={(url) => handleImageUpload('quarter2', index, 'questionImage', url)}
                               />
                               {question.questionImage && (
                                 <div className="relative mt-2">
                                   <img src={question.questionImage} alt="" className="max-h-32 rounded border" />
                                   <button
                                     className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                                     onClick={() => handleImageDelete('quarter2', index, 'questionImage')}
                                   >
                                     ✕
                                   </button>
                                 </div>
                               )}
                             </div>
                             
                             {/* Answer Image */}
                             <div className="mb-3">
                               <label className="block text-gray-300 text-sm font-medium mb-1">Answer Image</label>
                               <ImageUploader
                                 label="Upload Answer Image"
                                 storagePath={`game-images/admin/${editingGame.id}/quarter2-q${index + 1}-answer`}
                                 onUpload={(url) => handleImageUpload('quarter2', index, 'answerImage', url)}
                               />
                               {question.answerImage && (
                                 <div className="relative mt-2">
                                   <img src={question.answerImage} alt="" className="max-h-32 rounded border" />
                                   <button
                                     className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                                     onClick={() => handleImageDelete('quarter2', index, 'answerImage')}
                                   >
                                     ✕
                                   </button>
                                 </div>
                               )}
                             </div>
                             
                             {/* Creator Comment */}
                             <div className="mb-3">
                               <label className="block text-gray-300 text-sm font-medium mb-1">
                                 💡 Creator Comment (Optional)
                               </label>
                               <textarea
                                 placeholder="Explain hints, song clues, or provide context for this specific question..."
                                 value={question.creatorComment || ''}
                                 onChange={(e) => handleEditChange('quarter2', index, 'creatorComment', e.target.value)}
                                 className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                                 rows="3"
                               />
                               <p className="text-xs text-gray-400 mt-1">
                                 Optional: Explain how song hints relate to the answer, provide context, or share inside jokes
                               </p>
                             </div>
                           </div>
                         ))}
                       </div>
                     )}

                                         {/* Quarter 3 */}
                     {editingGame.quarter3 && (
                       <div className="mb-6">
                         <h4 className="text-md font-medium text-gray-300 mb-3">Quarter 3</h4>
                         {editingGame.quarter3.map((question, index) => (
                           <div key={index} className="bg-gray-700 p-4 rounded mb-3">
                             <div className="mb-3">
                               <input
                                 type="text"
                                 placeholder="Category"
                                 value={question.category || ''}
                                 onChange={(e) => handleEditChange('quarter3', index, 'category', e.target.value)}
                                 className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                               />
                             </div>
                             <textarea
                               placeholder="Question"
                               value={question.question || ''}
                               onChange={(e) => handleEditChange('quarter3', index, 'question', e.target.value)}
                               className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm mb-2"
                               rows="2"
                             />
                             <input
                               type="text"
                               placeholder="Answer"
                               value={question.answer || ''}
                               onChange={(e) => handleEditChange('quarter3', index, 'answer', e.target.value)}
                               className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm mb-3"
                             />
                             
                             {/* Question Image */}
                             <div className="mb-3">
                               <label className="block text-gray-300 text-sm font-medium mb-1">Question Image</label>
                               <ImageUploader
                                 label="Upload Question Image"
                                 storagePath={`game-images/admin/${editingGame.id}/quarter3-q${index + 1}-question`}
                                 onUpload={(url) => handleImageUpload('quarter3', index, 'questionImage', url)}
                               />
                               {question.questionImage && (
                                 <div className="relative mt-2">
                                   <img src={question.questionImage} alt="" className="max-h-32 rounded border" />
                                   <button
                                     className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                                     onClick={() => handleImageDelete('quarter3', index, 'questionImage')}
                                   >
                                     ✕
                                   </button>
                                 </div>
                               )}
                             </div>
                             
                             {/* Answer Image */}
                             <div className="mb-3">
                               <label className="block text-gray-300 text-sm font-medium mb-1">Answer Image</label>
                               <ImageUploader
                                 label="Upload Answer Image"
                                 storagePath={`game-images/admin/${editingGame.id}/quarter3-q${index + 1}-answer`}
                                 onUpload={(url) => handleImageUpload('quarter3', index, 'answerImage', url)}
                               />
                               {question.answerImage && (
                                 <div className="relative mt-2">
                                   <img src={question.answerImage} alt="" className="max-h-32 rounded border" />
                                   <button
                                     className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                                     onClick={() => handleImageDelete('quarter3', index, 'answerImage')}
                                   >
                                     ✕
                                   </button>
                                 </div>
                               )}
                             </div>
                             
                             {/* Creator Comment */}
                             <div className="mb-3">
                               <label className="block text-gray-300 text-sm font-medium mb-1">
                                 💡 Creator Comment (Optional)
                               </label>
                               <textarea
                                 placeholder="Explain hints, song clues, or provide context for this specific question..."
                                 value={question.creatorComment || ''}
                                 onChange={(e) => handleEditChange('quarter3', index, 'creatorComment', e.target.value)}
                                 className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                                 rows="3"
                               />
                               <p className="text-xs text-gray-400 mt-1">
                                 Optional: Explain how song hints relate to the answer, provide context, or share inside jokes
                               </p>
                             </div>
                           </div>
                         ))}
                       </div>
                     )}

                                         {/* Quarter 4 */}
                     {editingGame.quarter4 && (
                       <div className="mb-6">
                         <h4 className="text-md font-medium text-gray-300 mb-3">Quarter 4</h4>
                         {editingGame.quarter4.map((question, index) => (
                           <div key={index} className="bg-gray-700 p-4 rounded mb-3">
                             <div className="mb-3">
                               <input
                                 type="text"
                                 placeholder="Category"
                                 value={question.category || ''}
                                 onChange={(e) => handleEditChange('quarter4', index, 'category', e.target.value)}
                                 className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                               />
                             </div>
                             <textarea
                               placeholder="Question"
                               value={question.question || ''}
                               onChange={(e) => handleEditChange('quarter4', index, 'question', e.target.value)}
                               className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm mb-2"
                               rows="2"
                             />
                             <input
                               type="text"
                               placeholder="Answer"
                               value={question.answer || ''}
                               onChange={(e) => handleEditChange('quarter4', index, 'answer', e.target.value)}
                               className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm mb-3"
                             />
                             
                             {/* Question Image */}
                             <div className="mb-3">
                               <label className="block text-gray-300 text-sm font-medium mb-1">Question Image</label>
                               <ImageUploader
                                 label="Upload Question Image"
                                 storagePath={`game-images/admin/${editingGame.id}/quarter4-q${index + 1}-question`}
                                 onUpload={(url) => handleImageUpload('quarter4', index, 'questionImage', url)}
                               />
                               {question.questionImage && (
                                 <div className="relative mt-2">
                                   <img src={question.questionImage} alt="" className="max-h-32 rounded border" />
                                   <button
                                     className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                                     onClick={() => handleImageDelete('quarter4', index, 'questionImage')}
                                   >
                                     ✕
                                   </button>
                                 </div>
                               )}
                             </div>
                             
                             {/* Answer Image */}
                             <div className="mb-3">
                               <label className="block text-gray-300 text-sm font-medium mb-1">Answer Image</label>
                               <ImageUploader
                                 label="Upload Answer Image"
                                 storagePath={`game-images/admin/${editingGame.id}/quarter4-q${index + 1}-answer`}
                                 onUpload={(url) => handleImageUpload('quarter4', index, 'answerImage', url)}
                               />
                               {question.answerImage && (
                                 <div className="relative mt-2">
                                   <img src={question.answerImage} alt="" className="max-h-32 rounded border" />
                                   <button
                                     className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                                     onClick={() => handleImageDelete('quarter4', index, 'answerImage')}
                                   >
                                     ✕
                                   </button>
                                 </div>
                               )}
                             </div>
                             
                             {/* Creator Comment */}
                             <div className="mb-3">
                               <label className="block text-gray-300 text-sm font-medium mb-1">
                                 💡 Creator Comment (Optional)
                               </label>
                               <textarea
                                 placeholder="Explain hints, song clues, or provide context for this specific question..."
                                 value={question.creatorComment || ''}
                                 onChange={(e) => handleEditChange('quarter4', index, 'creatorComment', e.target.value)}
                                 className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                                 rows="3"
                               />
                               <p className="text-xs text-gray-400 mt-1">
                                 Optional: Explain how song hints relate to the answer, provide context, or share inside jokes
                               </p>
                             </div>
                           </div>
                         ))}
                       </div>
                     )}

                                         {/* Halftime Question */}
                     {editingGame.halftime && (
                       <div className="mb-6">
                         <h4 className="text-md font-medium text-gray-300 mb-3">Halftime Question</h4>
                         <div className="bg-gray-700 p-4 rounded">
                           <div className="mb-3">
                             <input
                               type="text"
                               placeholder="Category"
                               value={editingGame.halftime.category || ''}
                               onChange={(e) => setEditingGame({
                                 ...editingGame,
                                 halftime: { ...editingGame.halftime, category: e.target.value }
                               })}
                               className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                             />
                           </div>
                           <textarea
                             placeholder="Question"
                             value={editingGame.halftime.question || ''}
                             onChange={(e) => setEditingGame({
                               ...editingGame,
                               halftime: { ...editingGame.halftime, question: e.target.value }
                             })}
                             className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm mb-2"
                             rows="2"
                           />
                           <input
                             type="text"
                             placeholder="Answer"
                             value={editingGame.halftime.answer || ''}
                             onChange={(e) => setEditingGame({
                               ...editingGame,
                               halftime: { ...editingGame.halftime, answer: e.target.value }
                             })}
                             className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm mb-3"
                           />
                           
                           {/* Question Image */}
                           <div className="mb-3">
                             <label className="block text-gray-300 text-sm font-medium mb-1">Question Image</label>
                             <ImageUploader
                               label="Upload Question Image"
                               storagePath={`game-images/admin/${editingGame.id}/halftime-question`}
                               onUpload={(url) => setEditingGame({
                                 ...editingGame,
                                 halftime: { ...editingGame.halftime, questionImage: url }
                               })}
                             />
                             {editingGame.halftime.questionImage && (
                               <div className="relative mt-2">
                                 <img src={editingGame.halftime.questionImage} alt="" className="max-h-32 rounded border" />
                                 <button
                                   className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                                   onClick={() => setEditingGame({
                                     ...editingGame,
                                     halftime: { ...editingGame.halftime, questionImage: '' }
                                   })}
                                 >
                                   ✕
                                 </button>
                               </div>
                             )}
                           </div>
                           
                           {/* Answer Image */}
                           <div className="mb-3">
                             <label className="block text-gray-300 text-sm font-medium mb-1">Answer Image</label>
                             <ImageUploader
                               label="Upload Answer Image"
                               storagePath={`game-images/admin/${editingGame.id}/halftime-answer`}
                               onUpload={(url) => setEditingGame({
                                 ...editingGame,
                                 halftime: { ...editingGame.halftime, answerImage: url }
                               })}
                             />
                             {editingGame.halftime.answerImage && (
                               <div className="relative mt-2">
                                 <img src={editingGame.halftime.answerImage} alt="" className="max-h-32 rounded border" />
                                 <button
                                   className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                                   onClick={() => setEditingGame({
                                     ...editingGame,
                                     halftime: { ...editingGame.halftime, answerImage: '' }
                                   })}
                                 >
                                   ✕
                                 </button>
                               </div>
                             )}
                           </div>
                           
                           {/* Creator Comment */}
                           <div className="mb-3">
                             <label className="block text-gray-300 text-sm font-medium mb-1">
                               💡 Creator Comment (Optional)
                             </label>
                             <textarea
                               placeholder="Explain hints, song clues, or provide context for this specific question..."
                               value={editingGame.halftime.creatorComment || ''}
                               onChange={(e) => setEditingGame({
                                 ...editingGame,
                                 halftime: { ...editingGame.halftime, creatorComment: e.target.value }
                               })}
                               className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                               rows="3"
                             />
                             <p className="text-xs text-gray-400 mt-1">
                               Optional: Explain how song hints relate to the answer, provide context, or share inside jokes
                             </p>
                           </div>
                         </div>
                       </div>
                     )}

                                         {/* Final Question */}
                     {editingGame.final && (
                       <div className="mb-6">
                         <h4 className="text-md font-medium text-gray-300 mb-3">Final Question</h4>
                         <div className="bg-gray-700 p-4 rounded">
                           <div className="mb-3">
                             <input
                               type="text"
                               placeholder="Category"
                               value={editingGame.final.category || ''}
                               onChange={(e) => setEditingGame({
                                 ...editingGame,
                                 final: { ...editingGame.final, category: e.target.value }
                               })}
                               className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                             />
                           </div>
                           <textarea
                             placeholder="Question"
                             value={editingGame.final.question || ''}
                             onChange={(e) => setEditingGame({
                               ...editingGame,
                               final: { ...editingGame.final, question: e.target.value }
                             })}
                             className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm mb-2"
                             rows="2"
                           />
                           <input
                             type="text"
                             placeholder="Answer"
                             value={editingGame.final.answer || ''}
                             onChange={(e) => setEditingGame({
                               ...editingGame,
                               final: { ...editingGame.final, answer: e.target.value }
                             })}
                             className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm mb-3"
                           />
                           
                           {/* Question Image */}
                           <div className="mb-3">
                             <label className="block text-gray-300 text-sm font-medium mb-1">Question Image</label>
                             <ImageUploader
                               label="Upload Question Image"
                               storagePath={`game-images/admin/${editingGame.id}/final-question`}
                               onUpload={(url) => setEditingGame({
                                 ...editingGame,
                                 final: { ...editingGame.final, questionImage: url }
                               })}
                             />
                             {editingGame.final.questionImage && (
                               <div className="relative mt-2">
                                 <img src={editingGame.final.questionImage} alt="" className="max-h-32 rounded border" />
                                 <button
                                   className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                                   onClick={() => setEditingGame({
                                     ...editingGame,
                                     final: { ...editingGame.final, questionImage: '' }
                                   })}
                                 >
                                   ✕
                                 </button>
                               </div>
                             )}
                           </div>
                           
                           {/* Answer Image */}
                           <div className="mb-3">
                             <label className="block text-gray-300 text-sm font-medium mb-1">Answer Image</label>
                             <ImageUploader
                               label="Upload Answer Image"
                               storagePath={`game-images/admin/${editingGame.id}/final-answer`}
                               onUpload={(url) => setEditingGame({
                                 ...editingGame,
                                 final: { ...editingGame.final, answerImage: url }
                               })}
                             />
                             {editingGame.final.answerImage && (
                               <div className="relative mt-2">
                                 <img src={editingGame.final.answerImage} alt="" className="max-h-32 rounded border" />
                                 <button
                                   className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                                   onClick={() => setEditingGame({
                                     ...editingGame,
                                     final: { ...editingGame.final, answerImage: '' }
                                   })}
                                 >
                                   ✕
                                 </button>
                               </div>
                             )}
                           </div>
                           
                           {/* Creator Comment */}
                           <div className="mb-3">
                             <label className="block text-gray-300 text-sm font-medium mb-1">
                               💡 Creator Comment (Optional)
                             </label>
                             <textarea
                               placeholder="Explain hints, song clues, or provide context for this specific question..."
                               value={editingGame.final.creatorComment || ''}
                               onChange={(e) => setEditingGame({
                                 ...editingGame,
                                 final: { ...editingGame.final, creatorComment: e.target.value }
                               })}
                               className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                               rows="3"
                             />
                             <p className="text-xs text-gray-400 mt-1">
                               Optional: Explain how song hints relate to the answer, provide context, or share inside jokes
                             </p>
                           </div>
                         </div>
                       </div>
                     )}

                     {/* Tiebreaker Question */}
                     {editingGame.tieBreaker && (
                       <div className="mb-6">
                         <h4 className="text-md font-medium text-gray-300 mb-3">Tiebreaker Question</h4>
                         <div className="bg-gray-700 p-4 rounded">
                           <div className="mb-3">
                             <input
                               type="text"
                               placeholder="Category"
                               value={editingGame.tieBreaker.category || ''}
                               onChange={(e) => setEditingGame({
                                 ...editingGame,
                                 tieBreaker: { ...editingGame.tieBreaker, category: e.target.value }
                               })}
                               className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                             />
                           </div>
                           <textarea
                             placeholder="Question"
                             value={editingGame.tieBreaker.question || ''}
                             onChange={(e) => setEditingGame({
                               ...editingGame,
                               tieBreaker: { ...editingGame.tieBreaker, question: e.target.value }
                             })}
                             className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm mb-2"
                             rows="2"
                           />
                           <input
                             type="text"
                             placeholder="Answer"
                             value={editingGame.tieBreaker.answer || ''}
                             onChange={(e) => setEditingGame({
                               ...editingGame,
                               tieBreaker: { ...editingGame.tieBreaker, answer: e.target.value }
                             })}
                             className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm mb-3"
                           />
                           
                           {/* Question Image */}
                           <div className="mb-3">
                             <label className="block text-gray-300 text-sm font-medium mb-1">Question Image</label>
                             <ImageUploader
                               label="Upload Question Image"
                               storagePath={`game-images/admin/${editingGame.id}/tiebreaker-question`}
                               onUpload={(url) => setEditingGame({
                                 ...editingGame,
                                 tieBreaker: { ...editingGame.tieBreaker, questionImage: url }
                               })}
                             />
                             {editingGame.tieBreaker.questionImage && (
                               <div className="relative mt-2">
                                 <img src={editingGame.tieBreaker.questionImage} alt="" className="max-h-32 rounded border" />
                                 <button
                                   className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                                   onClick={() => setEditingGame({
                                     ...editingGame,
                                     tieBreaker: { ...editingGame.tieBreaker, questionImage: '' }
                                   })}
                                 >
                                   ✕
                                 </button>
                               </div>
                             )}
                           </div>
                           
                           {/* Answer Image */}
                           <div className="mb-3">
                             <label className="block text-gray-300 text-sm font-medium mb-1">Answer Image</label>
                             <ImageUploader
                               label="Upload Answer Image"
                               storagePath={`game-images/admin/${editingGame.id}/tiebreaker-answer`}
                               onUpload={(url) => setEditingGame({
                                 ...editingGame,
                                 tieBreaker: { ...editingGame.tieBreaker, answerImage: url }
                               })}
                             />
                             {editingGame.tieBreaker.answerImage && (
                               <div className="relative mt-2">
                                 <img src={editingGame.tieBreaker.answerImage} alt="" className="max-h-32 rounded border" />
                                 <button
                                   className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                                   onClick={() => setEditingGame({
                                     ...editingGame,
                                     tieBreaker: { ...editingGame.tieBreaker, answerImage: '' }
                                   })}
                                 >
                                   ✕
                                 </button>
                               </div>
                             )}
                           </div>
                           
                           {/* Creator Comment */}
                           <div className="mb-3">
                             <label className="block text-gray-300 text-sm font-medium mb-1">
                               💡 Creator Comment (Optional)
                             </label>
                             <textarea
                               placeholder="Explain hints, song clues, or provide context for this specific question..."
                               value={editingGame.tieBreaker.creatorComment || ''}
                               onChange={(e) => setEditingGame({
                                 ...editingGame,
                                 tieBreaker: { ...editingGame.tieBreaker, creatorComment: e.target.value }
                               })}
                               className="w-full p-2 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                               rows="3"
                             />
                             <p className="text-xs text-gray-400 mt-1">
                               Optional: Explain how song hints relate to the answer, provide context, or share inside jokes
                             </p>
                           </div>
                         </div>
                       </div>
                     )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-700">
                    <button
                      onClick={closeEditModal}
                      className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      disabled={saving}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMarketplace; 